/**
 * *! This helper has unit tests.
 * If you make any changes to this file, you should run the unit tests:
 * npm test src/utest/unit_helpers.test.ts
 */

/**
 * Formats a numeric value with a metric prefix (k, M, G) if the unit supports it.
 * Supported units include common SI and derived units that are typically scaled with metric prefixes.
 * Also supports length units (mm, cm, m) cascading to cm, m, km.
 *
 * Supported units:
 * - W   (watt)        – power
 * - Wh  (watt-hour)   – energy (power over time)
 * - Hz  (hertz)       – frequency (cycles per second)
 * - Pa  (pascal)      – pressure
 * - J   (joule)       – energy or work
 * - N   (newton)      – force
 * - B   (byte)        – digital information
 * - mol (mole)        – amount of substance
 * - lm  (lumen)       – luminous flux
 * - lx  (lux)         – illuminance (lumen per square meter)
 * - mm  (millimeter)  – length
 * - cm  (centimeter)  – length
 * - m   (meter)       – length
 *
 * Examples:
 *   convertToUnit(1500, 'W')    → "1.5 kW"
 *   convertToUnit(2500000, 'J') → "2.5 MJ"
 *   convertToUnit(4200, 'Hz')   → "4.2 kHz"
 *   convertToUnit(100650, 'lx') → "100.65 klx"
 *   convertToUnit(1200000000, 'B') → "1.2 GB"
 *   convertToUnit(12345, 'mm')  → "1.23 m"
 *
 * @param {number} value - The numeric value to convert.
 * @param {string} unit - The current unit of the value (e.g. "C", "Pa").
 * @param {{ fixed?: number, dontConvert?: boolean }} params - Optional settings:
 *   - fixed: number of decimal places to round to (default: 1)
 *   - dontConvert: if true, skips unit conversion and returns raw value + unit
 * @returns {string} - The formatted value followed by its unit (e.g. "2.5 kWh").
 */
export const convertToUnit = (
	value: number,
	unit: string = '',
	params = { fixed: 1, dontConvert: false },
): string => {
	const converted = convertToValueAndUnit(value, unit, params);
	return `${converted.value} ${converted.unit}`;
};

/**
 * Converts a numeric value into a formatted string with its unit,
 * based on the specified measurement type and unit system.
 *
 * @param {number} value - The numeric value to convert.
 * @param {string} unit - The current unit of the value (e.g. "C", "Pa").
 * @param {{ fixed?: number, dontConvert?: boolean }} params - Optional settings:
 *   - fixed: number of decimal places to round to (default: 1)
 *   - dontConvert: if true, skips unit conversion and returns raw value + unit
 * @returns {{ value: number | string, unit: string }} - An object containing the formatted value and unit. value is string for ft/in (e.g. "5′6″").
 */
export const convertToValueAndUnit = (
	value: number,
	unit: string = '',
	params = { fixed: 1, dontConvert: false },
): {
	value: number | string;
	unit: string;
} => {
	if (typeof value !== 'number') {
		return { value, unit };
	}
	let fixed = params?.fixed || 1;
	if (params?.dontConvert === true) {
		return { value: round(value, unit === 'in' ? 2 : fixed), unit };
	}
	if (/^(W|Wh|W\/h|Hz|Pa|J|N|B|mol|lm|lx)$/i.test(unit)) {
		const absValue = Math.abs(value);
		if (absValue >= 1_000_000_000) {
			unit = `G${unit}`;
			value /= 1_000_000_000;
		} else if (absValue >= 1_000_000) {
			unit = `M${unit}`;
			value /= 1_000_000;
		} else if (absValue >= 1_000) {
			unit = `k${unit}`;
			value /= 1_000;
		}
	} else if (/^(mm|cm|m)$/i.test(unit)) {
		fixed = fixed == 1 ? 2 : fixed;
		unit = unit.toLowerCase();
		if (unit === 'mm' && value >= 10) {
			unit = 'cm';
			value /= 10;
		}
		if (unit === 'cm' && value >= 100) {
			unit = 'm';
			value /= 100;
		}
		if (unit === 'm' && value >= 1_000) {
			unit = 'km';
			value /= 1000;
		}
	} else if (/^(ft|in)$/i.test(unit)) {
		const mmPerInch = 25.4;

		// always convert mm → inches
		let inches = value / mmPerInch;

		// Convert inches to feet and inches if greater than or equal to 12 inches
		const feet = Math.floor(inches / 12);
		const remainingInches = inches % 12;

		if (feet >= 1) {
			return {
				value: `${feet}′${round(remainingInches, fixed)}″`,
				unit: '', // already formatted in combined ft/in form
			};
		} else {
			return {
				value: `${round(inches, fixed)}″`,
				unit: '', // already formatted in combined ft/in form
			};
		}
	}
	return { value: round(value, fixed), unit };
};

export const round = (value: number, fixed = 1): number => {
	if (value == null || !Number.isFinite(value)) return 0;
	const scale = 10 ** fixed;
	return Number((Math.round((value + Number.EPSILON) * scale) / scale).toFixed(fixed));
};

export const skipUnitConversion = (measurementType: string): boolean => {
	// Precipitation unit is always in millimeters (mm), no conversion needed
	return measurementType === 'precipitation';
};
